import { PostgrestClient } from '@supabase/postgrest-js'
import { logErrorToService } from '../utils/errorLogger'
import { getAuthToken, getSessionId } from './auth/tokenBridge'

const NEON_BASE_URL = import.meta.env.VITE_NEON_API_URL

if (!NEON_BASE_URL) {
  console.warn(
    '[postgrest] VITE_NEON_API_URL is not set. ' +
    'PostgREST calls will fail until it is configured in your .env file.',
  )
}

/**
 * Custom fetch wrapper that:
 * - Injects the authentication Bearer token on every request.
 * - Injects x-session-id when available.
 * - Reports all PostgREST errors to Sentry: network-level failures (fetch throws)
 *   and HTTP error responses (status >= 400).
 */
const authedFetch: typeof fetch = async (input, init = {}) => {
  const token = await getAuthToken()
  const sessionId = getSessionId()

  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (sessionId) headers.set('x-session-id', sessionId)

  let response: Response
  try {
    response = await fetch(input, { ...init, headers })
  } catch (networkError: unknown) {
    // Catches network-level failures: offline, DNS errors, TCP timeouts.
    // These never produce a Response so they bypass the status check below.
    logErrorToService(
      networkError instanceof Error
        ? networkError
        : new Error('PostgREST network request failed'),
      {
        context: 'postgrest',
        severity: 'high',
        sessionId: sessionId ?? undefined,
        url: getRequestUrl(input),
        method: init.method ?? 'GET',
      },
    )
    throw networkError
  }

  if (response.status >= 400) {
    const postgrestError = await readPostgrestError(response)

    logErrorToService(new Error(postgrestError.message ?? `PostgREST request failed with status ${response.status}`), {
      context: 'postgrest',
      status: response.status,
      code: postgrestError.code,
      sessionId: sessionId ?? undefined,
      severity: response.status >= 500 ? 'high' : 'medium',
      url: getRequestUrl(input),
      method: init.method ?? 'GET',
    })
  }

  return response
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input
  }

  if (input instanceof URL) {
    return input.toString()
  }

  return input.url
}

async function readPostgrestError(response: Response): Promise<{ message?: string; code?: string }> {
  try {
    const payload = await response.clone().json() as { message?: unknown; code?: unknown }

    return {
      message: typeof payload.message === 'string' ? payload.message : undefined,
      code: typeof payload.code === 'string' ? payload.code : undefined,
    }
  } catch {
    return {}
  }
}

/**
 * Singleton PostgrestClient pointed at the Neon Data API.
 *
 * Usage:
 *   import { db } from '../lib/postgrest'
 *   const { data, error } = await db.from('profiles').select('*').eq('id', userId).single()
 */
export const db = new PostgrestClient(NEON_BASE_URL ?? '', { fetch: authedFetch })
